import type { ComponentType } from 'react';
import * as en from './blog-data.en';
import * as ko from './blog-data.ko';
import type { BlogArticleMetadata, BlogPost, BlogVideo } from './blog-types';

export type BlogData = {
  videos: BlogVideo[];
  posts: BlogPost[];
};

const BLOG_DATA: Record<string, BlogData> = {
  ko: {
    videos: ko.BLOG_VIDEOS,
    posts: ko.BLOG_POSTS,
  },
  en: {
    videos: en.BLOG_VIDEOS,
    posts: en.BLOG_POSTS,
  },
};

export function getBlogData(locale: string): BlogData {
  return BLOG_DATA[locale] ?? BLOG_DATA.ko;
}

// 국문(기본) 데이터 — slug/date 등 locale 무관한 정적 생성(sitemap, generateStaticParams)용.
export const BLOG_POSTS = ko.BLOG_POSTS;

// 아티클 MDX 모듈 로더. 본문(default)과 metadata 를 함께 반환한다.
// 미지원 locale 은 ko 로 폴백, 존재하지 않는 slug 는 null.
export async function loadBlogArticle(
  slug: string,
  locale: string,
): Promise<{
  default: ComponentType;
  metadata: BlogArticleMetadata;
} | null> {
  const lang = locale === 'en' ? 'en' : 'ko';
  // slug 는 generateStaticParams/BLOG_POSTS 로 한정되지만, 직접 URL 접근 대비 방어.
  if (!/^[\w-]+$/.test(slug)) return null;
  try {
    return await import(`../content/${slug}.${lang}.mdx`);
  } catch {
    return null;
  }
}

// 이전/다음 글 네비게이션. slug/href 만 반환하고, 제목은 호출부가 locale 데이터에서 조회한다.
// 순서·존재 판정도 locale 데이터 기준으로 한다(ko/en parity 가 깨져도 안전).
export function getArticleNav(
  slug: string,
  locale: string,
): {
  prev?: { slug: string; href: string };
  next?: { slug: string; href: string };
} {
  const posts = getBlogData(locale).posts;
  const idx = posts.findIndex((p) => p.slug === slug);
  if (idx === -1) return {};
  const prev = idx < posts.length - 1 ? posts[idx + 1] : undefined;
  const next = idx > 0 ? posts[idx - 1] : undefined;
  return {
    prev: prev ? { slug: prev.slug, href: `blog/${prev.slug}` } : undefined,
    next: next ? { slug: next.slug, href: `blog/${next.slug}` } : undefined,
  };
}
